import React, { useState } from 'react';
import { useNavigate } from 'react-router';
import { Trade } from '../ActiveTradesPage';
import MagicButton from 'components/MagicButton/MagicButton';
import { Points } from 'components/Points/Points';
import PackingSlipModal from './PackingSlipModal';
import ShippingCountdown from './ShippingCountdown';
import { useMarkAsShippedMutation, useRemoveCardFromTradeMutation } from 'store/services/trade/tradeApi';
// Removed admin API import - causing 401 for non-admin users
// import { useGetTradeFeeQuery } from 'store/services/admin/adminApi';
import { toast } from 'react-toastify';
import { AssetUrls } from 'config/assets';
import { getAvatarPlaceholder } from 'utils/avatarUtils';
import { ContactUserModal } from 'components/ContactUserModal/ContactUserModal';
import { SetIcon } from 'components/SetIcon';
import { FoilCardImage } from 'components/FoilCardImage';
import './TradeCard.css';

interface Props {
  trade: Trade;
}

// Format condition to short acronym
const formatCondition = (condition: string): string => {
  const conditionMap: { [key: string]: string } = {
    'NEAR_MINT': 'NM',
    'NM': 'NM',
    'SLIGHTLY_PLAYED': 'LP',
    'LIGHTLY_PLAYED': 'LP',
    'LP': 'LP',
    'SP': 'LP',
    'MODERATELY_PLAYED': 'MP',
    'MP': 'MP',
    'HEAVILY_PLAYED': 'HP',
    'HP': 'HP',
    'DAMAGED': 'DMG',
    'DMG': 'DMG'
  };
  return conditionMap[condition] || condition;
};

// Format language code to short code (normalize variations)
const formatLanguage = (language: string): string => {
  const languageMap: { [key: string]: string } = {
    'EN': 'EN',
    'ENGLISH': 'EN',
    'FR': 'FR',
    'FRENCH': 'FR',
    'DE': 'DE',
    'GERMAN': 'DE',
    'ES': 'ES',
    'SPANISH': 'ES',
    'IT': 'IT',
    'ITALIAN': 'IT',
    'PT': 'PT',
    'PORTUGUESE': 'PT',
    'JP': 'JP',
    'JA': 'JP',
    'JAPANESE': 'JP',
    'KR': 'KR',
    'KO': 'KR',
    'KOREAN': 'KR',
    'RU': 'RU',
    'RUSSIAN': 'RU',
    'CN': 'CN',
    'ZH': 'CN',
    'CHINESE': 'CN',
    'TW': 'TW',
    'NL': 'NL',
    'DUTCH': 'NL',
    'PL': 'PL',
    'POLISH': 'PL',
    'CS': 'CS',
    'CZECH': 'CS',
    'HU': 'HU',
    'HUNGARIAN': 'HU'
  };
  return languageMap[language.toUpperCase()] || language.toUpperCase().slice(0, 2);
};

export default function TradeCard({ trade }: Props) {
  const [isPackingSlipOpen, setIsPackingSlipOpen] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [markAsShipped, { isLoading: isMarkingShipped }] = useMarkAsShippedMutation();
  const [removeCardFromTrade, { isLoading: isRemovingCard }] = useRemoveCardFromTradeMutation();

  const handleContactClick = () => {
    setIsContactModalOpen(true);
  };

  const handleSayThanksClick = () => {
    setIsContactModalOpen(true);
  };
  const navigate = useNavigate();
  
  // Use the trade fee from the trade data itself, or default to 1%
  // Don't fetch from admin API - causes 401 for non-admin users
  const feePercentage = trade.tradeFee ?
    Math.round((trade.tradeFee / trade.totalValue) * 100) : 1;

  const handleMarkAsShipped = async () => {
    try {
      const result = await markAsShipped({ tradeId: trade.id }).unwrap();
      if (result.success) {
        toast.success('Trade marked as shipped successfully!');
      } else {
        toast.error(result.message || 'Failed to mark trade as shipped');
      }
    } catch (error: any) {
      console.error('Error marking trade as shipped:', error);
      toast.error(error?.data?.message || 'Failed to mark trade as shipped');
    }
  };

  const handleRemoveCard = async (item: any, itemIndex: number) => {
    // Only allow removing cards from trades that are ready (not shipped or completed)
    if (trade.status !== 'ready') {
      return;
    }

    try {
      const result = await removeCardFromTrade({
        tradeId: trade.id,
        itemIndex
      }).unwrap();

      if (result.success) {
        toast.success(`${item.name} removed from trade. ${result.refundedPoints || 0} points refunded to receiver.`);
      } else {
        toast.error(result.message || 'Failed to remove card from trade');
      }
    } catch (error: any) {
      console.error('Error removing card from trade:', error);
      toast.error(error?.data?.message || 'Failed to remove card from trade');
    }
  };

  const handleTradeAgain = () => {
    // Navigate to /sendcards with the trader's username as search filter
    navigate(`/sendcards?search=${encodeURIComponent(trade.traderName)}`);
  };

  const getStatusTitle = () => {
    switch (trade.status) {
      case 'ready':
        return 'Ready to ship';
      case 'transit':
        return 'Shipped';
      case 'completed':
        return 'Arrived';
      default:
        return '';
    }
  };

  const getStatusLabel = () => {
    switch (trade.status) {
      case 'ready':
        return 'You will mail:';
      case 'transit':
        return 'You sent:';
      case 'completed':
        return 'You sent:';
      default:
        return '';
    }
  };


  const renderStarRating = (rating: number) => {
    // Ensure rating is between 0 and 5
    const safeRating = Math.max(0, Math.min(5, rating || 0));
    const emptyStars = Math.max(0, 5 - safeRating);
    return '★'.repeat(safeRating) + '☆'.repeat(emptyStars);
  };

  return (
    <div className="trade-card">
      <div className="trade-card-left">
        <div className="package-title-row">
          <h3 className="trade-status-title">{getStatusTitle()}</h3>
          <Points points={trade.totalValue} type="big" />
        </div>
        
        <div className="trade-info">
          <div className="trade-detail">
            <span className="trade-label">Trade#</span>
            <span className="trade-value">{trade.tradeNumber}</span>
          </div>
          <div className="trade-detail">
            <span className="trade-label">Initiated:</span>
            <span className="trade-value">{trade.initiated}</span>
          </div>
          {/* Always show Point Fee row, even if value is 0 or undefined */}
          <div className="trade-detail">
            <span className="trade-label-with-tooltip">
              <span className="trade-label">Point Fee:</span>
              <img
                src="https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/icons/tooltip.svg"
                alt="Info"
                className="fee-info-icon"
              />
              <span className="fee-tooltip">
                To maintain the long-term sustainability of our trading economy, we apply a flexible trade fee of {feePercentage}% to each completed trade.
              </span>
            </span>
            <span className="trade-value">{trade.tradeFee || 0}</span>
          </div>
          {trade.shipped && (
            <div className="trade-detail">
              <span className="trade-label">Shipped:</span>
              <span className="trade-value">{trade.shipped}</span>
            </div>
          )}
          {trade.completed && (
            <div className="trade-detail">
              <span className="trade-label">Completed:</span>
              <span className="trade-value">{trade.completed}</span>
            </div>
          )}
        </div>

        {trade.status === 'ready' && (
          <ShippingCountdown initiatedDate={trade.initiatedRaw || trade.initiated} />
        )}

        {trade.status === 'transit' && trade.estimatedDelivery && (
          <div className="estimated-delivery">
            <span className="delivery-label">Estimated time to arrive</span>
            <div className="delivery-time">{trade.estimatedDelivery}</div>
            <div className="delivery-progress">
              <div className="progress-bar"></div>
            </div>
          </div>
        )}

        {trade.status === 'completed' && (
          <div className="trader-rating">
            <div className="rating-row">
              <span className="rating-label">Speed:</span>
              <span className="rating-stars">{renderStarRating(trade.speed)}</span>
            </div>
            <div className="rating-row">
              <span className="rating-label">Accuracy:</span>
              <span className="rating-stars">{renderStarRating(trade.accuracy)}</span>
            </div>
            <div className="rating-row">
              <span className="rating-label">Communication:</span>
              <span className="rating-stars">{renderStarRating(trade.communication)}</span>
            </div>
          </div>
        )}

        <div className="trade-actions">
          {trade.status === 'ready' && (
            <>
              <div className="action-buttons-row">
                <MagicButton
                  icon={
                    <img
                      src="https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/icons/contact.svg"
                      alt="Contact"
                      style={{ width: 18, height: 18, filter: 'brightness(0) invert(1)' }}
                    />
                  }
                  label="Contact"
                  color="dark-grey"
                  onPress={handleContactClick}
                />
                <MagicButton
                  icon={
                    <img
                      src={`${AssetUrls.base}/assets/icons/SendCards/icon-print.svg`}
                      alt="Print"
                      style={{ width: 18, height: 18, filter: 'brightness(0) invert(1)' }}
                    />
                  }
                  label="Packing Slip"
                  color="dark-grey"
                  onPress={() => setIsPackingSlipOpen(true)}
                />
              </div>
              <MagicButton
                icon={
                  <img
                    src={`${AssetUrls.base}/assets/icons/SendCards/truck-in-transit.svg`}
                    alt="Truck"
                    style={{ width: 18, height: 18, filter: 'brightness(0) invert(1)' }}
                  />
                }
                label={isMarkingShipped ? "Marking..." : "Mark shipped"}
                color="purple"
                onPress={handleMarkAsShipped}
                isDisabled={isMarkingShipped}
              />
            </>
          )}
          {trade.status === 'transit' && (
            <>
              <MagicButton
                icon={
                  <img
                    src="https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/icons/contact.svg"
                    alt="Contact"
                    style={{ width: 18, height: 18, filter: 'brightness(0) invert(1)' }}
                  />
                }
                label="Contact"
                color="dark-grey"
                onPress={handleContactClick}
              />
              <MagicButton
                icon={
                  <img
                    src={`${AssetUrls.base}/assets/icons/SendCards/icon-print.svg`}
                    alt="Print"
                    style={{ width: 18, height: 18, filter: 'brightness(0) invert(1)' }}
                  />
                }
                label="Packing Slip"
                color="dark-grey"
                onPress={() => setIsPackingSlipOpen(true)}
              />
            </>
          )}
          {trade.status === 'completed' && (
            <>
              <MagicButton
                icon={
                  <img
                    src="https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/icons/contact.svg"
                    alt="Say Thanks"
                    style={{ width: 18, height: 18, filter: 'brightness(0) invert(1)' }}
                  />
                }
                label="Say Thanks"
                color="dark-grey"
                onPress={handleSayThanksClick}
              />
              <MagicButton
                icon={
                  <img
                    src="https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/logos/arevakhach-icon.svg"
                    alt="Trade Again"
                    style={{ width: 18, height: 18, filter: 'brightness(0) invert(1)' }}
                  />
                }
                label="Trade again"
                color="dark-grey"
                onPress={handleTradeAgain}
              />
            </>
          )}
        </div>
      </div>

      <div className="trade-card-right">
        <div className="trader-info-row">
          <div className="status-label-left">
            <span className="status-text">{getStatusLabel()}</span>
          </div>
          <div className="trader-info">
            {trade.traderAvatar ? (
              <img 
                src={trade.traderAvatar} 
                alt={trade.traderName}
                className="trader-avatar"
              />
            ) : (() => {
              const placeholder = getAvatarPlaceholder(trade.traderName);
              return placeholder ? (
                <div 
                  className="trader-avatar trader-avatar-placeholder"
                  style={{ backgroundColor: placeholder.color }}
                >
                  <span className="trader-avatar-letter">
                    {placeholder.letter}
                  </span>
                </div>
              ) : (
                <div className="trader-avatar trader-avatar-placeholder">
                  <span className="trader-avatar-letter">?</span>
                </div>
              );
            })()}
            <span className="trader-name">{trade.traderName}</span>
          </div>
        </div>


        <div className="items-table">
          <div className="table-header">
            <span className="col-qty">Qty</span>
            <span className="col-name">Name</span>
            <span className="col-set">Set</span>
            <span className="col-foil">Foil</span>
            <span className="col-condition">Condition</span>
            <span className="col-language">Language</span>
            <span className="col-points">Points</span>
            <span className="col-remove"></span>
          </div>
          
          {trade.items.map((item, index) => (
            <div key={index} className="table-row">
              <span className="col-qty">{item.qty}</span>
              <div className="col-name">
                <FoilCardImage
                  src={AssetUrls.cards.ensureCDNUrl(item.image)?.replace('/large/', '/small/')?.replace('/normal/', '/small/') || ''}
                  alt={item.name}
                  className="card-thumbnail"
                  foil={item.foil}
                  borderRadius="4px"
                />
                <span>{item.name}</span>
              </div>
              <div className="col-set" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <SetIcon setIconUrl={item.setIconUrl} setName={item.set} />
                <span>{item.setCode || item.set}</span>
              </div>
              <span className="col-foil">{item.foil ? 'Foil' : '—'}</span>
              <span className="col-condition">{formatCondition(item.condition)}</span>
              <span className="col-language">{formatLanguage(item.language)}</span>
              <div className="col-points">
                <Points points={(item.pointValue || 0) * item.qty} type="small" />
              </div>
              <div className="col-remove">
                {trade.status === 'ready' && (
                  <img
                    src={AssetUrls.icons.dropdowns.close}
                    alt="Remove"
                    className={`remove-icon ${isRemovingCard ? 'removing' : ''}`}
                    onClick={() => handleRemoveCard(item, index)}
                    style={{ 
                      cursor: isRemovingCard ? 'not-allowed' : 'pointer',
                      opacity: isRemovingCard ? 0.5 : 1
                    }}
                  />
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <PackingSlipModal
        trade={trade}
        isOpen={isPackingSlipOpen}
        onClose={() => setIsPackingSlipOpen(false)}
      />

      <ContactUserModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        receiverId={parseInt(trade.traderId)}
        receiverUsername={trade.traderName}
        receiverAvatar={trade.traderAvatar}
        tradeId={trade.id}
      />
    </div>
  );
}